import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

/**
 * Smart PWA install banner — bottom toast for Chromium browsers.
 * Receives state from PWAInstallPrompt parent; renders nothing unless showBanner is true.
 */
export default function InstallBanner({ showBanner, triggerInstall, dismiss, dismissPermanent }) {
    const { t } = useTranslation()

    return (
        <AnimatePresence>
            {showBanner && (
                <motion.div
                    key="install-banner"
                    initial={{ y: 120, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 120, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    className="fixed bottom-6 left-4 right-4 z-[9999] mx-auto max-w-md
                               rounded-2xl border border-primary/30 bg-sable
                               shadow-[0_8px_40px_rgba(0,0,0,0.55),0_0_20px_rgba(255,222,89,0.12)]
                               backdrop-blur-md"
                    role="alert"
                    aria-label={t('pwa.install_title')}
                    id="pwa-install-banner"
                >
                    {/* Content row */}
                    <div className="flex items-center gap-3.5 px-5 pt-5 pb-3">
                        {/* App icon */}
                        <img
                            src="/logo.webp"
                            alt="JD Rivero"
                            width={48}
                            height={48}
                            className="h-12 w-12 rounded-xl border border-white/10 bg-black object-contain p-1 flex-shrink-0"
                        />

                        {/* Text */}
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-cloud tracking-wide">
                                {t('pwa.install_title')}
                            </p>
                            <p className="mt-0.5 text-xs text-cloud/60 leading-snug">
                                {t('pwa.install_desc')}
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 px-5 pb-4 pt-1">
                        <button
                            onClick={triggerInstall}
                            className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-ink
                                       transition-all duration-200 hover:bg-primary-dark hover:shadow-lg
                                       active:scale-[0.97]"
                            id="pwa-install-btn"
                        >
                            {t('pwa.install_btn')}
                        </button>
                        <button
                            onClick={dismiss}
                            className="rounded-lg border border-white/10 bg-white/5 px-4 py-2.5
                                       text-sm font-medium text-cloud/80 transition-all duration-200
                                       hover:bg-white/10 active:scale-[0.97]"
                            id="pwa-dismiss-btn"
                        >
                            {t('pwa.not_now')}
                        </button>
                    </div>

                    {/* Permanent dismiss link */}
                    <div className="border-t border-white/5 px-5 py-2.5 text-center">
                        <button
                            onClick={dismissPermanent}
                            className="text-[11px] text-cloud/30 transition-colors hover:text-cloud/50"
                            id="pwa-dismiss-permanent-btn"
                        >
                            {t('pwa.dont_ask')}
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
